import { z } from 'zod';
import { AnalysisConfidenceSchema } from './document-analysis.schemas';
import { refersTo } from '../common/refers-to';

// The Objekt memory (IMM-1050) — every statement immoOS read about an Objekt, one
// entry per statement, each with where and how it was read, plus statements an operator
// added by hand (`OPERATOR`, IMM-1157.4). Mirrors the backend `MemorySourceType` token
// (`domain/property-memory/memory-source.ts`).
export const MEMORY_SOURCE_TYPES = ['DOCUMENT', 'EMAIL', 'CALL', 'NOTE', 'OPERATOR'] as const;
export const MemorySourceTypeSchema = z.enum(MEMORY_SOURCE_TYPES);
export type MemorySourceType = z.infer<typeof MemorySourceTypeSchema>;

/** True for a source immoOS read; false for OPERATOR, a statement an operator added by hand (IMM-1157.4). The one membership test (BL-049). */
export function isReadMemorySource(type: MemorySourceType): boolean {
  return type !== 'OPERATOR';
}

export const PropertyMemoryEntrySchema = z
  .object({
    id: z.uuid(),
    // What the statement is about — the taxonomy label for a field reading, the
    // model's own German label for any other statement.
    label: z.string(),
    // The value as read. Never overwritten by a correction.
    value: z.unknown(),
    // A member's correction of the read value (IMM-366.4), or null. The effective
    // value is `correction.value` when present, else `value`.
    correction: z.object({ value: z.unknown(), correctedAt: z.iso.datetime() }).nullable(),
    // The Objekt field the statement fits, or null.
    fieldKey: z.string().nullable(),
    // `name` is the document's current display name, an e-mail's subject, or null
    // when the source has no name (a call or a note without subject).
    source: z.object({ type: MemorySourceTypeSchema, id: z.uuid(), name: z.string().nullable() }),
    passage: z.object({ page: z.number().int().positive().nullable(), excerpt: z.string().nullable() }),
    // The managed prompt that read it, as of the read (`immo_admin.skills`). `prompt` and `model`
    // are null exactly when `source.type` is `OPERATOR`: nobody read it.
    prompt: z.object({ kind: z.string(), key: z.string(), version: z.number().int().positive() }).nullable(),
    model: z.string().nullable(),
    confidence: AnalysisConfidenceSchema,
    readAt: z.iso.datetime(),
  })
  .refine(
    (entry) => {
      const read = isReadMemorySource(entry.source.type);

      return read === (entry.prompt !== null) && read === (entry.model !== null);
    },
    { message: 'prompt and model are null exactly on an OPERATOR entry', path: ['prompt'] },
  );

export const PropertyMemorySchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  entries: z.array(PropertyMemoryEntrySchema),
});

export type PropertyMemoryEntry = z.infer<typeof PropertyMemoryEntrySchema>;
export type PropertyMemory = z.infer<typeof PropertyMemorySchema>;

/** The fields an operator may re-file on an entry (IMM-1157.2). One spelling for the entity, the tool and the event. */
export const MEMORY_EDITABLE_FIELDS = ['label', 'fieldKey', 'confidence'] as const;

export type MemoryEditableField = (typeof MEMORY_EDITABLE_FIELDS)[number];

/** An operator named on an operator-side change (IMM-1157). `name` is null when the admin no longer resolves. */
export const MemoryOperatorSchema = z.object({ adminId: z.uuid(), name: z.string().nullable() });

/**
 * An entry as operators see it (IMM-1157): the dossier's entry plus who changed it on the
 * admin side. Admin surfaces only — the dossier, and with it the partner API, never names an operator.
 */
export const OperatorMemoryEntrySchema = PropertyMemoryEntrySchema.extend({
  operator: z.object({
    // The operator who added the statement by hand (IMM-1157.4); null on every read entry.
    addedBy: MemoryOperatorSchema.nullable(),
    // Whose correction `correction` is when an operator made it; null when uncorrected or a member corrected it.
    correctedBy: MemoryOperatorSchema.nullable(),
    // The operator's edit of how the statement is filed (IMM-1157.2); null when unedited. The
    // entry's own label, fieldKey and confidence are the edited ones; `read` holds the as-read
    // value of each field the edit changes, and only those.
    edited: z
      .object({
        by: MemoryOperatorSchema,
        at: z.iso.datetime(),
        read: z.object({
          label: z.string().optional(),
          fieldKey: z.string().nullable().optional(),
          confidence: AnalysisConfidenceSchema.optional(),
        }),
      })
      .nullable(),
    // Set when the source's latest reading no longer says this statement (IMM-1157.3): the row is kept
    // for its operator change, with the reading it had. Orphans are listed here and never in a dossier.
    orphanedAt: z.iso.datetime().nullable(),
    // The operator who deleted the statement — only ever set on an orphan, since a live deletion is not listed.
    deleted: z.object({ by: MemoryOperatorSchema, at: z.iso.datetime() }).nullable(),
  }),
});

export const OperatorPropertyMemorySchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  entries: z.array(OperatorMemoryEntrySchema),
});

export const DeletedMemoryEntrySchema = z.object({
  entryId: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  deletedAt: z.iso.datetime(),
});

export type MemoryOperator = z.infer<typeof MemoryOperatorSchema>;
export type OperatorMemoryEntry = z.infer<typeof OperatorMemoryEntrySchema>;
export type OperatorPropertyMemory = z.infer<typeof OperatorPropertyMemorySchema>;
export type DeletedMemoryEntry = z.infer<typeof DeletedMemoryEntrySchema>;
